'use client'

import { FC } from 'react'
import type { PaymentMethod, TicketProps } from '@/root/shared-types/reservation/reservation-types'
import { useGetTranslations } from '@/root/shared-hooks'
import { testIds } from '@/root/shared-consts'
import { useBookTicket } from '@/root/modules/shared/ConfirmPage'
import { CustomerInfo } from '@/root/modules/tuuleliinid/BookTicket/components/CustomerInfo'
import { ShoppingCart } from '@/root/modules/tuuleliinid/BookTicket/components/ShoppingCart/ShoppingCart'
import { TermsAndConditions } from '@/root/modules/tuuleliinid/BookTicket/components/TermsAndConditions/TermsAndConditions'
import { PaymentMethods } from '@/root/modules/tuuleliinid/BookTicket/components/PaymentMethods/PaymentMethods'
import { BottomNavButtons } from '@/root/components/tuuleliinid/BottomNavButtons/BottomNavButtons'

interface BookTicketProps extends TicketProps {
  paymentMethods: PaymentMethod[]
}

export const BookTicket: FC<BookTicketProps> = (props) => {
  const {
    there,
    back,
    itemColumns,
    isRoundTrip,
    amountFormatted,
    chargesTable,
    confirmationTotalText,
    terms,
    isAgree,
    onAgree,
    customerFormProps,
    creditBalance,
    isFreeTicket,
    backHref,
    paymentMethods,
    selectedMethod,
    onSelect,
    isCreditInsufficient,
    isNextDisabled,
    onProceed,
  } = useBookTicket(props)

  const { getTranslation } = useGetTranslations()
  const { privatePersonForm } = props

  return (
    <div data-testid={testIds.confirm.bookWrapper} className="px-5 py-10 lg:px-0">
      <div className="max-w-content mx-auto flex w-full flex-col items-start gap-8">
        {/* Page Title */}
        <h1 className="text-primary-base w-full text-center text-2xl font-bold">{getTranslation('checkout')}</h1>

        {/* Two Column Layout */}
        <div className="flex w-full flex-col items-start gap-5 lg:flex-row">
          {/* Left Column - Customer Info Form */}
          <CustomerInfo companyForm={true} privatePersonForm={privatePersonForm} {...customerFormProps} />

          {/* Right Column - Shopping Cart */}
          <ShoppingCart
            there={there}
            back={back}
            isRoundTrip={isRoundTrip}
            chargesTable={chargesTable}
            totalText={confirmationTotalText}
            totalAmount={amountFormatted}
            columns={itemColumns}
          />
        </div>

        {/* Payment Methods */}
        {!isFreeTicket && (
          <PaymentMethods
            paymentMethods={paymentMethods}
            onSelect={onSelect}
            selectedMethod={selectedMethod}
            creditBalance={creditBalance}
            isCreditInsufficient={isCreditInsufficient}
          />
        )}

        {/* Terms and Conditions */}
        <TermsAndConditions isAgree={isAgree} onAgree={onAgree} terms={terms} />

        {/* Bottom navigation */}
        <BottomNavButtons
          className="mt-12 mb-8 p-4"
          backHref={backHref}
          backTitle={getTranslation('backButton')}
          backSubtitle={getTranslation('backToTicketSelection')}
          nextTitle={getTranslation(isFreeTicket ? 'confirmFreeButtonTitle' : 'payButtonTitle')}
          nextSubtitle={getTranslation(isFreeTicket ? 'confirmFreeButtonSubtitle' : 'payButtonSubtitle')}
          nextDisabled={isNextDisabled}
          onNext={onProceed}
        />
      </div>
    </div>
  )
}
